
import * as util from 'util'

import * as big from '../big'
import * as int64 from '../int64'
import * as uuid from '../uuid'

import * as fbe from '../fbe'

const Big = big.Big // eslint-disable-line
const Int64 = int64.Int64 // eslint-disable-line
const UInt64 = int64.UInt64 // eslint-disable-line
const UUID = uuid.UUID // eslint-disable-line

/**
 * Date struct
 */
class Date {
  /**
   * Initialize struct
   * @param {!number=} year
   * @param {!number=} month
   * @param {!number=} day
   * @constructor
   */
  constructor (year = 0, month = 0, day = 0) {
    this.Year = year
    this.Month = month
    this.Day = day
  }

  /**
   * Copy struct (shallow copy)
   * @this {!Date}
   * @param {!Date} other Other struct
   * @returns {!Date} This struct
   */
  copy (other) {
    if (other.Year != null) {
      this.Year = other.Year
    } else {
      this.Year = null
    }
    if (other.Month != null) {
      this.Month = other.Month
    } else {
      this.Month = null
    }
    if (other.Day != null) {
      this.Day = other.Day
    } else {
      this.Day = null
    }
    return this
  }

  /**
   * Clone struct (deep clone)
   * @this {!Date}
   * @returns {!Date} Cloned struct
   */
  clone () {
    // Serialize the struct to the FBE stream
    let writer = new DateModel(new fbe.WriteBuffer())
    writer.serialize(this)

    // Deserialize the struct from the FBE stream
    let reader = new DateModel(new fbe.ReadBuffer())
    reader.attachBuffer(writer.buffer)
    return reader.deserialize().value
  }

  /**
   * Is this struct equal to other one?
   * @this {!Date}
   * @param {!Date} other Other struct
   * @returns {boolean} Equal result
   */
  eq (other) {
    if (!(other instanceof Date)) {
      throw new TypeError('Instance of Date is required!')
    }
    return true
  }

  /**
   * Convert struct to JSON
   * @this {!Date}
   * @returns {!object} Struct value for JSON
   */
  toJSON () {
    return {
      Year: ((this.Year != null) ? this.Year : null),
      Month: ((this.Month != null) ? this.Month : null),
      Day: ((this.Day != null) ? this.Day : null)
    }
  }

  /**
   * Convert JSON to struct
   * @param {!string} json JSON string
   * @returns {!object} Struct value for JSON
   */
  static fromJSON (json) {
    return Date.fromObject(JSON.parse(json))
  }

  /**
   * Create struct from object value
   * @param {!Date} other Object value
   * @returns {!Date} Created struct
   */
  static fromObject (other) {
    return new Date().copy(other)
  }

  /**
   * Get the FBE type
   * @this {!Date}
   * @returns {!number} FBE type
   */
  get fbeType () {
    return Date.fbeType
  }

  /**
   * Get the FBE type (static)
   * @this {!Date}
   * @returns {!number} FBE type
   */
  static get fbeType () {
    return 207
  }
}

export { Date };

/**
 * Fast Binary Encoding Date field model
 */
class FieldModelDate extends fbe.FieldModel {
  /**
   * Initialize field model with the given buffer and offset
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Buffer
   * @param {!number} offset Offset
   * @constructor
   */
  constructor (buffer, offset) {
    super(buffer, offset)
    this.Year = new fbe.FieldModelUInt16(buffer, 4 + 4)
    this.Month = new fbe.FieldModelUInt8(buffer, this.Year.fbeOffset + this.Year.fbeSize)
    this.Day = new fbe.FieldModelUInt8(buffer, this.Month.fbeOffset + this.Month.fbeSize)
  }

  /**
   * Get the field size
   * @this {!FieldModelDate}
   * @returns {!number} Field size
   */
  get fbeSize () {
    return 4
  }

  /**
   * Get the field body size
   * @this {!FieldModelDate}
   * @returns {!number} Field body size
   */
  get fbeBody () {
    return 4 + 4 + this.Year.fbeSize + this.Month.fbeSize + this.Day.fbeSize
  }

  /**
   * Get the field extra size
   * @this {!FieldModelDate}
   * @returns {!number} Field extra size
   */
  get fbeExtra () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4) > this._buffer.size)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)

    let fbeResult = this.fbeBody + this.Year.fbeExtra + this.Month.fbeExtra + this.Day.fbeExtra

    this._buffer.unshift(fbeStructOffset)

    return fbeResult
  }

  /**
   * Get the field type
   * @this {!FieldModelDate}
   * @returns {!number} Field type
   */
  get fbeType () {
    return FieldModelDate.fbeType
  }

  /**
   * Get the field type (static)
   * @this {!FieldModelDate}
   * @returns {!number} Field type
   */
  static get fbeType () {
    return 207
  }

  /**
   * Check if the struct value is valid
   * @this {!FieldModelDate}
   * @param {!boolean} fbeVerifyType Verify model type flag, defaults is true
   * @returns {!boolean} Field model valid state
   */
  verify (fbeVerifyType = true) {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return true
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return false
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    if (fbeStructSize < (4 + 4)) {
      return false
    }

    let fbeStructType = this.readUInt32(fbeStructOffset + 4)
    if (fbeVerifyType && (fbeStructType !== this.fbeType)) {
      return false
    }

    this._buffer.shift(fbeStructOffset)
    let fbeResult = this.verifyFields(fbeStructSize)
    this._buffer.unshift(fbeStructOffset)
    return fbeResult
  }

  /**
   * Check if the struct fields are valid
   * @this {!FieldModelDate}
   * @param {!number} fbeStructSize FBE struct size
   * @returns {!boolean} Field model valid state
   */
  verifyFields (fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.Year.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.Year.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Year.fbeSize

    if ((fbeCurrentSize + this.Month.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.Month.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Month.fbeSize

    if ((fbeCurrentSize + this.Day.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.Day.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Day.fbeSize

    return true
  }

  /**
   * Get the struct value (begin phase)
   * @this {!FieldModelDate}
   * @returns {!number} Field model begin offset
   */
  getBegin () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + 4 + 4) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return 0
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    console.assert((fbeStructSize >= (4 + 4)), 'Model is broken!')
    if (fbeStructSize < (4 + 4)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Get the struct value (end phase)
   * @this {!FieldModelDate}
   * @param {!number} fbeBegin Field model begin offset
   */
  getEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Get the struct value
   * @this {!FieldModelDate}
   * @param {!Date} fbeValue Default value, defaults is new Date()
   * @returns {!Date} Date value
   */
  get (fbeValue = new Date()) {
    let fbeBegin = this.getBegin()
    if (fbeBegin === 0) {
      return fbeValue
    }

    let fbeStructSize = this.readUInt32(0)
    this.getFields(fbeValue, fbeStructSize)
    this.getEnd(fbeBegin)
    return fbeValue
  }

  /**
   * Get the struct fields values
   * @this {!FieldModelDate}
   * @param {!Date} fbeValue Date value
   * @param {!number} fbeStructSize Struct size
   */
  getFields (fbeValue, fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.Year.fbeSize) <= fbeStructSize) {
      fbeValue.Year = this.Year.get()
    } else {
      fbeValue.Year = 0
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Year.fbeSize

    if ((fbeCurrentSize + this.Month.fbeSize) <= fbeStructSize) {
      fbeValue.Month = this.Month.get()
    } else {
      fbeValue.Month = 0
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Month.fbeSize

    if ((fbeCurrentSize + this.Day.fbeSize) <= fbeStructSize) {
      fbeValue.Day = this.Day.get()
    } else {
      fbeValue.Day = 0
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Day.fbeSize
  }

  /**
   * Set the struct value (begin phase)
   * @this {!FieldModelDate}
   * @returns {!number} Field model begin offset
   */
  setBegin () {
    console.assert(((this._buffer.offset + this.fbeOffset + this.fbeSize) <= this._buffer.size), 'Model is broken!')
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructSize = this.fbeBody
    let fbeStructOffset = this._buffer.allocate(fbeStructSize) - this._buffer.offset
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + fbeStructSize) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset <= 0) || ((this._buffer.offset + fbeStructOffset + fbeStructSize) > this._buffer.size)) {
      return 0
    }

    this.writeUInt32(this.fbeOffset, fbeStructOffset)
    this.writeUInt32(fbeStructOffset, fbeStructSize)
    this.writeUInt32(fbeStructOffset + 4, this.fbeType)

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Set the struct value (end phase)
   * @this {!FieldModelDate}
   * @param {!number} fbeBegin Field model begin offset
   */
  setEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Set the struct value
   * @this {!FieldModelDate}
   * @param {!Date} fbeValue Date value
   */
  set (fbeValue) {
    let fbeBegin = this.setBegin()
    if (fbeBegin === 0) {
      return
    }

    this.setFields(fbeValue)
    this.setEnd(fbeBegin)
  }

  /**
   * Set the struct fields values
   * @this {!FieldModelDate}
   * @param {Date} fbeValue Date value
   */
  setFields (fbeValue) {
    this.Year.set(fbeValue.Year)
    this.Month.set(fbeValue.Month)
    this.Day.set(fbeValue.Day)
  }
}

export { FieldModelDate };

/**
 * Fast Binary Encoding Date model
 */
class DateModel extends fbe.Model {
  /**
   * Initialize model with the given buffer
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Read/Write buffer, defaults is new fbe.WriteBuffer()
   * @constructor
   */
  constructor (buffer = new fbe.WriteBuffer()) {
    super(buffer)
    this._model = new FieldModelDate(this.buffer, 4)
  }

  /**
   * Get the Date model
   * @this {!DateModel}
   * @returns {!FieldModelDate} model Date model
   */
  get model () {
    return this._model
  }

  /**
   * Get the model size
   * @this {!DateModel}
   * @returns {!number} Model size
   */
  get fbeSize () {
    return this.model.fbeSize + this.model.fbeExtra
  }

  /**
   * Get the model type
   * @this {!DateModel}
   * @returns {!number} Model type
   */
  get fbeType () {
    return DateModel.fbeType
  }

  /**
   * Get the model type (static)
   * @this {!DateModel}
   * @returns {!number} Model type
   */
  static get fbeType () {
    return FieldModelDate.fbeType
  }

  /**
   * Check if the struct value is valid
   * @this {!DateModel}
   * @returns {!boolean} Model valid state
   */
  verify () {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return false
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    if (fbeFullSize < this.model.fbeSize) {
      return false
    }

    return this.model.verify()
  }

  /**
   * Create a new model (begin phase)
   * @this {!DateModel}
   * @returns {!number} Model begin offset
   */
  createBegin () {
    return this.buffer.allocate(4 + this.model.fbeSize)
  }

  /**
   * Create a new model (end phase)
   * @this {!DateModel}
   * @param {!number} fbeBegin Model begin offset
   */
  createEnd (fbeBegin) {
    let fbeEnd = this.buffer.size
    let fbeFullSize = fbeEnd - fbeBegin
    this.writeUInt32(this.model.fbeOffset - 4, fbeFullSize)
    return fbeFullSize
  }

  /**
   * Serialize the struct value
   * @this {!DateModel}
   * @param {!Date} value Date value
   * @return {!number} Model begin offset
   */
  serialize (value) {
    let fbeBegin = this.createBegin()
    this.model.set(value)
    return this.createEnd(fbeBegin)
  }

  /**
   * Deserialize the struct value
   * @this {!DateModel}
   * @param {!Date} value Date value, defaults is new Date()
   * @return {!object} Deserialized Date value and its size
   */
  deserialize (value = new Date()) {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return { value: new Date(), size: 0 }
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    console.assert((fbeFullSize >= this.model.fbeSize), 'Model is broken!')
    if (fbeFullSize < this.model.fbeSize) {
      return { value: new Date(), size: 0 }
    }

    this.model.get(value)
    return { value: value, size: fbeFullSize }
  }

  /**
   * Move to the next struct value
   * @this {!DateModel}
   * @param {!number} prev Previous Date model size
   */
  next (prev) {
    this.model.fbeShift(prev)
  }
}

export { DateModel };
